import axios from 'axios';
import moment from 'moment';
import { Commit, ActionTree } from 'vuex';
import _ from 'lodash'
import { RootState } from '@/store/types';
import {
  StudentsState,
  StudentConditions,
  Student,
  CourseHistory,
  SpirHistory,
  WetecHistory,
  GradeHistory,
  SpecialConsideration,
  LessonStudentGrade,
  Point,
  FinalReviewSheet,
  CourseGrade,
} from '@/store/students/types';
import { getPagerFromApiResponse } from '../utils';
import { Group } from '../groups/types';
import { Sort } from '../sort.types';

/**
 * APIのレスポンスからStudentを作成
 * @param {any} student APIレスポンスの履修者（一人）
 */
const convertStudentFromApiResponse = (student: any): Student => {
  return {
    id: student.id,
    name: student.nameEn,
    nameJp: student.name,
    nickname: student.nickname,
    studentNumber: student.studentNumber,
    oldStudentNumber: student.oldStudentNumber,
    phone1: student.phone1,
    phone2: student.phone2,
    email1: student.email1,
    email2: student.email2,
    studentStatus: student.studentStatus,
    courseHistories: student.histories?.courses.map((course: any): CourseHistory => {
      return {
        no: course.no,
        courseHistoryId: course.courseHistoryId,
        courseName: course.courseName,
        specialConsiderations: course.specialConsiderations.map(
          (specialConsideration: any): SpecialConsideration => {
            return {
              specialConsideration: {
                code: specialConsideration.code,
                name: specialConsideration.name,
              },
              comment: specialConsideration.comment,
            };
          },
        ),
        wetecBefore: course.wetec.before,
        wetecAfter: course.wetec.after,
      }
    }),
    spirHistories: student.histories?.spirs.map((spir: any): SpirHistory => {
      return {
        no: spir.no,
        applicationId: spir.applicationId,
        fromCourseHistoryId: spir.fromCourseHistoryId,
        fromCourseName: spir.fromCourseName,
        toCourseHistoryId: spir.toCourseHistoryId,
        toCourseName: spir.toCourseName,
      }
    }),
    wetecHistories: student.histories?.wetecs.map((wetec: any): WetecHistory => {
      return {
        no: wetec.no,
        name: wetec.name,
        wetec: {
          total: wetec.total,
          first: wetec.first,
          second: wetec.second,
          third: wetec.third,
          fourth: wetec.fourth,
          fifth: wetec.fifth,
          sixth: wetec.sixth,
        },
      }
    }),
    gradeHistories: student.histories?.grades.map((grade: any): GradeHistory => {
      return {
        no: grade.no,
        applicationId: grade.applicationId,
        courseHistoryId: grade.courseHistoryId,
        courseName: grade.courseName,
        lessonUnit: grade.lessonUnit,
      }
    }),
    courseCodes: student?.courseCodes,
  }
}

const actions: ActionTree<StudentsState, RootState> = {
  /**
   * 履修者一括登録（CSVインポート）
   */
  importStudentsByCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/students/import', formData);
  },
  /**
   * 履修者一覧取得
   */
  fetchStudents: async (
    { commit }: { commit: Commit },
    { conditions, page, sortBy }: {
      conditions?: StudentConditions,
      page: number,
      sortBy?: Sort,
    },
  ): Promise<Student[]> => {
    const { headers, data } = await axios.get('/students', {
      params: {
        page,
        ..._.pickBy({
          sortBy: _.values(sortBy).join(':'),
          studentIds: conditions?.studentIds?.join(','),
          studentNumber: conditions?.studentNumber,
          oldStudentNumber: conditions?.oldStudentNumber,
          name: conditions?.studentNameJapanese,
          nameEn: conditions?.studentName,
          email: conditions?.email,
          universityCode: conditions?.university?.code,
          campusCodes: conditions?.campuses?.map(({ code }) => code).join(','),
          fiscalYear: conditions?.fiscalYear,
          semesterCode: conditions?.semester?.code,
          dayOfWeeks: conditions?.dayOfWeeks?.join(','),
          courseName: conditions?.courseName,
          levelCodes: conditions?.levels?.map(({ code }) => code).join(','),
          room: conditions?.classroom,
          boothNumber: conditions?.booth,
          periods: conditions?.periods?.join(','),
          tutorId: conditions?.tutorId,
          tutorName: conditions?.tutorName,
          specialConsiderationCode: conditions?.specialConsideration?.code,
          hasSpir: conditions?.isSpir ? 1 : null,
          hasInquiry: conditions?.isChangeGrade ? 1 : null,
          wetecId: conditions?.wetecId,
          hasNotWetecId: conditions?.hasNotWetecId,
          absencesCount: conditions?.absencesCount,
        }, (v) => {
          return _.isNumber(v) ? true : v
        }),
      },
    })

    const students: Student[] = _.map(data, convertStudentFromApiResponse)
    commit('fetchedStudents', {
      conditions,
      pager: getPagerFromApiResponse(page, headers),
      students,
      sortBy,
    });

    return students
  },

  /**
   * 履修者詳細
   */
  getStudent: async (
    { commit, getters, dispatch },
    { studentId } : { studentId: string },
  ) => {
    const { data } = await axios.get(`/students/${studentId}`)
    commit('gotStudent', convertStudentFromApiResponse(data))
  },

  /**
   * 履修者IDから履修者を一人取得
   * @param {{
   *  studentId: string, // 履修者ID
   * }}
   */
  getStudentById: async (
    { commit }: { commit: Commit },
    { studentId }: { studentId: string },
  ) => axios.get('/students', {
    params: { studentIds: studentId },
  })
    .then(({ data: students }) => {
      const student: any = _.first(students)
      commit('gotStudent', convertStudentFromApiResponse(student))
    }),

  /**
   * 履修者コース受講履歴取得
   */
  getStudentCourseGrade: async (
    { commit, dispatch, getters },
    { studentId, courseHistoryId }: { studentId: string, courseHistoryId: string },
  ) => {
    const { data } = await axios.get(`/students/${studentId}/courseHistories/${courseHistoryId}`)
    await Promise.all([
      ...data.groups.map(({ groupId }: { groupId: string }) => dispatch('getGroupById', { groupId })),
    ])

    const courseGrade: CourseGrade = {
      course: {
        id: data.courseId,
        name: data.courseName,
      },
      rawScore: data.rawScore,
      groups: _.map(data.groups, ({ groupId }): Group => getters.findGroupById(groupId)),
      specialConsiderations: _.map(data.specialConsiderations, (sc): SpecialConsideration => ({
        specialConsideration: {
          code: sc.specialConsiderationCode,
          name: sc.name,
        },
        comment: sc.comment,
      })),
      lessonStudentGrades: _.chain(data.lessonStudentGrade)
        .map((g): LessonStudentGrade => ({
          lessonId: g.lessonId,
          unit: g.unit,
          date: moment(g.date, 'YYYYMMDD'),
          period: g.period,
          preparation: g.preparation,
          points: _.map(g.grade.points, (point): Point => ({
            rubricId: point.rubricId,
            ratingId: point.ratingId,
            name: point.name,
            beforeDescription: _.find(g.grade.beforePoints, {
              rubricId: point.rubricId,
            })?.description || null,
            description: point.description,
            note: point.note,
          })),
          beforeComment: g.grade?.beforeComment || null,
          comment: g.grade?.comment,
          tutor: {
            id: g.grade?.createTutorId,
            name: g.grade?.createTutorName,
          },
        }))
        .sortBy(['unit'])
        .value(),
      finalReviewSheets: _.map(data.frss, (f): FinalReviewSheet => ({
        tutor: {
          id: f.frs.createTutorId,
          name: f.frs.createTutorName,
        },
        beforeComment: f.beforeFrs?.comment || null,
        comment: f.frs?.comment,
      })),
    }

    commit('gotStudentCourseGrade', {
      studentId,
      courseHistoryId,
      courseGrade,
    })
  },

  /**
   * 履修者のWetec情報一括インポート
   * @param {File} csvFile CSVファイル
   */
  importStudentWetecsCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/students/import/wetecs', formData);
  },

  /**
   * 履修者の特別配慮を更新
   */
  updateSpecialConsideration: async (
    { dispatch },
    { studentId, courseHistoryId, specialConsiderations } : {
      studentId: string,
      courseHistoryId: string,
      specialConsiderations: SpecialConsideration[]
    },
  ) => {
    await axios.put(`/students/${studentId}/specialConsideration`, {
      studentCourseHistoryId: courseHistoryId,
      specialConsiderations: specialConsiderations.map(
        (sc: SpecialConsideration) => {
          return {
            specialConsiderationCode: sc.specialConsideration?.code,
            comment: sc.comment,
          }
        },
      ),
    })
  },

  editStudent: async (
    { dispatch }, { id, student }: { id: string, student: Student },
  ) => {
    await axios.put(`/students/${id}`, {
      name: student.nameJp,
      nameEn: student.name,
      nickname: student.nickname,
      email1: student.email1,
      email2: student.email2,
      phone1: student.phone1,
      phone2: student.phone2,
      studentStatus: student.studentStatus,
    })
  },
};

export default actions;
